<template>
  <div id="passport-view">
    <AppHeader>Reckon Passport</AppHeader>

    <div class="passport-page">
      <v-container v-if="passport">
        <div class="passport-toolbar">
          <v-btn
            outlined
            class="back-link"
            :to="passport.playerCardRoute"
          >
            <v-icon left>mdi-arrow-left</v-icon>
            Meet the Heroes
          </v-btn>

          <nav class="crew-passport-nav" aria-label="Crew passport navigation">
            <router-link
              :to="passportRoute(previousPassport)"
              class="passport-nav-link"
              :aria-label="`View previous crew passport: ${previousPassport.fullName}`"
            >
              <v-icon aria-hidden="true">mdi-chevron-left</v-icon>
              <span>
                <small>Previous</small>
                <strong>{{ previousPassport.displayName }}</strong>
              </span>
            </router-link>
            <router-link
              :to="passportRoute(nextPassport)"
              class="passport-nav-link passport-nav-link--next"
              :aria-label="`View next crew passport: ${nextPassport.fullName}`"
            >
              <span>
                <small>Next</small>
                <strong>{{ nextPassport.displayName }}</strong>
              </span>
              <v-icon aria-hidden="true">mdi-chevron-right</v-icon>
            </router-link>
          </nav>
        </div>

        <section class="passport-hero">
          <div class="passport-brand-row">
            <div>
              <div class="passport-brand">Reckon Passport</div>
              <div class="passport-number">Passport No. {{ passport.passportNumber }}</div>
            </div>
            <div
              v-if="passport.status === 'alumni'"
              class="status-pill"
            >
              Alumni
            </div>
          </div>

          <div class="passport-hero-content">
            <button
              v-if="heroProfile"
              type="button"
              class="passport-portrait-card"
              :class="{ 'is-flipped': portraitFlipped }"
              :aria-label="portraitFlipLabel"
              :aria-pressed="portraitFlipped ? 'true' : 'false'"
              @click="portraitFlipped = !portraitFlipped"
            >
              <span class="passport-portrait-face passport-portrait-front">
                <img
                  :src="passport.headshot | mediaSrc"
                  :alt="`${passport.fullName} headshot`"
                />
              </span>
              <span class="passport-portrait-face passport-portrait-back">
                <img
                  :src="[heroProfile.heroImage, heroProfile.placeholderImage] | mediaSrc"
                  :alt="heroProfile.artAlt"
                />
              </span>
            </button>
            <v-img
              v-else
              :src="passport.headshot | mediaSrc"
              :alt="`${passport.fullName} headshot`"
              aspect-ratio="0.78"
              class="headshot"
            ></v-img>

            <div class="passport-details">
              <div class="kicker">Crew Member</div>
              <div
                v-if="passport.chickFilAFridayTrophy || passport.awards.length"
                class="passport-awards"
                aria-label="Passport awards"
              >
                <div
                  v-if="passport.chickFilAFridayTrophy"
                  class="passport-trophy"
                  :class="`passport-trophy--${passport.chickFilAFridayTrophy.tone}`"
                  :aria-label="`${passport.chickFilAFridayTrophy.place} in the Chick-fil-A Friday rankings`"
                >
                  <div class="passport-trophy-icon" aria-hidden="true">
                    <v-icon>mdi-trophy</v-icon>
                    <strong>{{ passport.chickFilAFridayTrophy.rank }}</strong>
                  </div>
                  <div class="passport-trophy-copy">
                    <span>Chick-fil-A Friday</span>
                    <strong>{{ passport.chickFilAFridayTrophy.place }}</strong>
                    <small>
                      {{ passport.chickFilAFridayTrophy.attendedEvents }} of
                      {{ passport.chickFilAFridayTrophy.eligibleEvents }} Fridays ·
                      {{ passport.chickFilAFridayTrophy.attendancePercentage }}%
                    </small>
                  </div>
                </div>

                <div
                  v-for="award in passport.awards"
                  :key="award.id"
                  class="passport-award"
                  :class="`passport-award--${award.tone}`"
                  :aria-label="`${award.name}, ${award.recognitionLabel}`"
                >
                  <div
                    class="passport-award-artwork"
                    :class="{ 'passport-award-artwork--light': award.image }"
                    aria-hidden="true"
                  >
                    <span v-if="award.artType === 'championship-belt'" class="championship-belt">
                      <strong>R</strong>
                    </span>
                    <img v-else-if="award.image" :src="award.image | mediaSrc" alt="" />
                    <v-icon v-else>{{ award.icon }}</v-icon>
                  </div>
                  <div class="passport-award-copy">
                    <span>{{ award.category }}</span>
                    <strong>{{ award.name }} · {{ award.recognitionLabel }}</strong>
                    <small>{{ award.description }}</small>
                  </div>
                </div>
              </div>
              <h1>{{ passport.fullName }}</h1>
              <p class="title-line">{{ passport.title }}</p>
              <p class="short-quote">"{{ passport.shortQuote }}"</p>

              <div class="passport-actions">
                <a
                  v-if="passport.linkedInUrl"
                  :href="passport.linkedInUrl"
                  class="passport-action-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon left small>mdi-linkedin</v-icon>
                  LinkedIn Profile
                </a>
                <router-link
                  v-if="heroProfile && portraitFlipped"
                  :to="{ name: 'hero-profile', params: { heroSlug: heroProfile.slug } }"
                  class="passport-action-link origin-action-link"
                >
                  <v-icon left small>mdi-flash-outline</v-icon>
                  Read Origin Story
                </router-link>
              </div>

              <div class="detail-grid">
                <div class="detail-item">
                  <span>Preferred Name</span>
                  <strong>{{ passport.preferredName }}</strong>
                </div>
                <div class="detail-item">
                  <span>{{ passport.crewMemberThrough ? 'Crew Member' : 'Crew Member Since' }}</span>
                  <strong>
                    {{ passport.crewMemberThrough
                      ? `${passport.crewMemberSince}–${passport.crewMemberThrough}`
                      : passport.crewMemberSince }}
                  </strong>
                </div>
                <div class="detail-item">
                  <span>Adventure Level</span>
                  <strong>{{ passport.adventureLevel }}</strong>
                  <small>{{ formatLevelRange(passport.adventureLevelDetails) }}</small>
                </div>
                <div class="detail-item">
                  <span>Location</span>
                  <strong>{{ passport.location }}</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section class="passport-section">
          <div class="section-heading">
            <p class="section-kicker">Stats</p>
            <h2>Adventure Stats</h2>
          </div>
          <div class="stat-grid">
            <router-link
              v-for="stat in statTiles"
              :key="stat.key"
              :to="stat.to"
              class="stat-tile stat-link"
            >
              <v-icon>{{ stat.icon }}</v-icon>
              <span>{{ stat.label }}</span>
              <strong>{{ stat.value }}</strong>
              <v-icon class="stat-arrow">mdi-arrow-right</v-icon>
            </router-link>
            <router-link
              :to="passport.stats.favoriteExperienceRoute"
              class="stat-tile stat-link"
            >
              <v-icon>mdi-star-outline</v-icon>
              <span>Favorite Experience</span>
              <strong>{{ passport.stats.favoriteExperience }}</strong>
              <v-icon class="stat-arrow">mdi-arrow-right</v-icon>
            </router-link>
          </div>
        </section>

        <section id="passport-stamps" class="passport-section">
          <div class="section-heading">
            <p class="section-kicker">Stamps</p>
            <h2>Passport Stamps</h2>
          </div>
          <div class="stamp-grid">
            <router-link
              v-for="stamp in passport.stamps"
              :key="stamp.slug"
              :to="stamp.sourceRoute"
              class="stamp-card"
            >
              <div class="stamp-type">{{ stamp.tripType }}</div>
              <h3>{{ stamp.tripName }}</h3>
              <p>{{ stamp.destination }}, {{ stamp.country }}</p>
              <span>{{ stamp.date }}</span>
              <v-icon class="card-arrow">mdi-arrow-right</v-icon>
            </router-link>
          </div>
        </section>

        <section class="passport-section two-column-section">
          <div>
            <div class="section-heading">
              <p class="section-kicker">Badges</p>
              <h2>Merit Badges</h2>
            </div>
            <div class="badge-list">
              <router-link
                v-for="badge in passport.badges"
                :key="`${badge.tripName}-${badge.name}`"
                :to="badgeRecordRoute(badge)"
                class="passport-badge-card"
                :aria-label="`View the adventure for ${badge.name}`"
              >
                <div class="passport-badge-artwork">
                  <v-img
                    v-if="badge.image"
                    :src="badge.image | mediaSrc"
                    :alt="badge.name"
                    aspect-ratio="1"
                    class="passport-badge-image"
                  />
                  <v-icon v-else class="passport-badge-placeholder">mdi-medal-outline</v-icon>
                </div>
                <div class="passport-badge-title">
                  <span>{{ badge.name }}</span>
                  <v-icon small aria-hidden="true">mdi-arrow-right</v-icon>
                </div>
              </router-link>
            </div>
          </div>

          <div>
            <div class="section-heading">
              <p class="section-kicker">Favorites</p>
              <h2>Favorite Memories</h2>
            </div>
            <div class="favorite-grid">
              <router-link
                v-for="favorite in favoriteTiles"
                :key="favorite.label"
                :to="favorite.to"
                class="favorite-card"
              >
                <span>{{ favorite.label }}</span>
                <strong>{{ favorite.value }}</strong>
                <v-icon class="card-arrow">mdi-arrow-right</v-icon>
              </router-link>
            </div>
          </div>
        </section>
      </v-container>

      <v-container v-else>
        <section class="passport-missing">
          <h1>Passport Not Found</h1>
          <p>This Reckon Passport is not available yet.</p>
          <v-btn
            outlined
            class="back-link"
            :to="{ name: 'reckon-way-people' }"
          >
            Back to Meet the Heroes
          </v-btn>
        </section>
      </v-container>
    </div>

    <AppFooter></AppFooter>
  </div>
</template>

<script>
import { passportBySlug, passportList } from '../data/passports';
import { heroProfileByCrewSlug } from '../data/heroProfiles';
import AppFooter from '../components/Footer.vue';
import AppHeader from '../components/Header.vue';

export default {
  name: 'PassportView',
  components: { AppFooter, AppHeader },
  data() {
    return {
      portraitFlipped: false,
    };
  },
  computed: {
    passport() {
      return passportBySlug[this.$route.params.employeeSlug];
    },
    passportIndex() {
      return passportList.findIndex((passport) => passport.slug === this.$route.params.employeeSlug);
    },
    previousPassport() {
      if (this.passportIndex < 0) return null;
      return passportList[(this.passportIndex - 1 + passportList.length) % passportList.length];
    },
    nextPassport() {
      if (this.passportIndex < 0) return null;
      return passportList[(this.passportIndex + 1) % passportList.length];
    },
    heroProfile() {
      return heroProfileByCrewSlug[this.$route.params.employeeSlug] || null;
    },
    portraitFlipLabel() {
      if (!this.heroProfile || !this.passport) {
        return '';
      }

      return this.portraitFlipped
        ? `Show ${this.passport.fullName} profile photo`
        : `Reveal ${this.heroProfile.heroName}`;
    },
    statTiles() {
      if (!this.passport) {
        return [];
      }

      return Object.values(this.passport.statDetails).map((stat) => ({
        ...stat,
        value: stat.count,
        to: {
          name: 'passport-stat',
          params: {
            employeeSlug: this.passport.slug,
            statKey: stat.key,
          },
        },
      }));
    },
    favoriteTiles() {
      if (!this.passport) {
        return [];
      }

      const { favorites } = this.passport;

      return [
        {
          label: 'Favorite Meal', value: favorites.favoriteMeal, to: favorites.favoriteMealRoute,
        },
        {
          label: 'Favorite Activity', value: favorites.favoriteActivity, to: favorites.favoriteActivityRoute,
        },
        {
          label: 'Favorite City', value: favorites.favoriteCity, to: favorites.favoriteCityRoute,
        },
      ];
    },
  },
  watch: {
    '$route.params.employeeSlug': function resetPortrait() {
      this.portraitFlipped = false;
    },
  },
  methods: {
    passportRoute(passport) {
      return {
        name: 'passport',
        params: { employeeSlug: passport.slug },
      };
    },
    badgeRecordRoute(badge) {
      return badge.activityRoute || {
        name: 'passport-record',
        params: {
          employeeSlug: this.passport.slug,
          statKey: 'badges',
          recordId: badge.id,
        },
      };
    },
    formatLevelRange(level) {
      if (!level) {
        return '';
      }

      if (level.maxBadges === null) {
        return `${level.minBadges}+ badges`;
      }

      return `${level.minBadges}-${level.maxBadges} badges`;
    },
  },
};
</script>

<style scoped>
.passport-page {
  background: #050505;
  color: #f5f5f5;
  padding: 48px 0 64px;
}

.back-link {
  border-color: #e23232 !important;
  color: #fff !important;
}

.passport-toolbar {
  align-items: center;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 24px;
}

.crew-passport-nav {
  display: flex;
  gap: 10px;
}

.passport-nav-link {
  align-items: center;
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 5px;
  color: #fff;
  display: flex;
  gap: 6px;
  min-width: 122px;
  padding: 7px 10px;
  text-decoration: none;
  transition: background-color 160ms ease, border-color 160ms ease, transform 160ms ease;
}

.passport-nav-link--next {
  justify-content: flex-end;
  text-align: right;
}

.passport-nav-link span {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}

.passport-nav-link small {
  color: #999;
  font-size: 0.62rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.passport-nav-link strong {
  font-family: "Oswald", sans-serif;
  font-size: 0.92rem;
}

.passport-nav-link .v-icon {
  color: #e23232;
}

.passport-nav-link:hover,
.passport-nav-link:focus-visible {
  background: #1c1c1c;
  border-color: #e23232;
  outline: none;
  transform: translateY(-1px);
}

.passport-hero {
  background:
    linear-gradient(135deg, rgba(226, 50, 50, 0.18), rgba(5, 5, 5, 0.96) 38%),
    #101010;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 28px;
}

.passport-brand-row,
.passport-hero-content,
.passport-actions,
.detail-grid,
.stat-grid,
.stamp-grid,
.two-column-section,
.favorite-grid,
.badge-list {
  display: grid;
  gap: 16px;
}

.passport-brand-row {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 28px;
}

.passport-brand {
  color: #fff;
  font-size: 0.9rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.passport-number,
.detail-item span,
.section-kicker,
.stamp-type,
.favorite-card span {
  color: #b8b8b8;
  font-size: 0.78rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.status-pill {
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 999px;
  color: #f5f5f5;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 6px 12px;
  text-transform: uppercase;
}

.passport-hero-content {
  grid-template-columns: minmax(220px, 290px) 1fr;
}

.headshot,
.passport-portrait-card,
.passport-portrait-face {
  border-radius: 6px;
}

.passport-portrait-card {
  aspect-ratio: 0.78;
  background: transparent;
  border: 0;
  color: #fff;
  cursor: pointer;
  display: block;
  padding: 0;
  position: relative;
  transform-style: preserve-3d;
  transition: box-shadow 180ms ease, transform 760ms cubic-bezier(0.2, 0.72, 0.2, 1);
  width: 100%;
}

.passport-portrait-card:focus-visible {
  box-shadow: 0 0 0 3px rgba(240, 32, 32, 0.8), 0 20px 38px rgba(0, 0, 0, 0.52);
  outline: 3px solid #fff;
  outline-offset: 5px;
  transform: translateY(-7px) scale(1.012);
}

.passport-portrait-card:hover {
  box-shadow: 0 0 0 3px rgba(240, 32, 32, 0.72), 0 20px 38px rgba(0, 0, 0, 0.52);
  transform: translateY(-7px) scale(1.012);
}

.passport-portrait-card.is-flipped {
  transform: rotateY(180deg);
}

.passport-portrait-card.is-flipped:hover,
.passport-portrait-card.is-flipped:focus-visible {
  transform: translateY(-7px) scale(1.012) rotateY(180deg);
}

.passport-portrait-card.is-flipped::after {
  animation: passport-edge-pulse 2.8s ease-in-out infinite;
  border: 1px solid rgba(147, 226, 255, 0.9);
  border-radius: 9px;
  box-shadow: 0 0 24px rgba(92, 205, 255, 0.48);
  content: '';
  inset: -5px;
  pointer-events: none;
  position: absolute;
}

.passport-portrait-face {
  backface-visibility: hidden;
  inset: 0;
  overflow: hidden;
  position: absolute;
}

.passport-portrait-face img {
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.passport-portrait-back {
  background: #07111a;
  transform: rotateY(180deg);
}

.passport-details h1 {
  color: #fff;
  font-size: clamp(2.2rem, 5vw, 4.25rem);
  letter-spacing: 0;
  line-height: 1;
  margin: 8px 0;
}

.kicker {
  color: #e23232;
  font-weight: 900;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.passport-awards {
  align-items: stretch;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

.passport-trophy {
  align-items: center;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.025));
  border: 1px solid var(--trophy-color);
  border-radius: 10px;
  box-shadow: 0 0 22px var(--trophy-glow);
  display: inline-flex;
  gap: 14px;
  max-width: 390px;
  padding: 12px 16px;
}

.passport-award {
  --award-color: #f6c94f;
  --award-glow: rgba(246, 201, 79, 0.16);
  align-items: center;
  background: linear-gradient(135deg, rgba(240, 32, 32, 0.13), rgba(246, 201, 79, 0.04));
  border: 1px solid var(--award-color);
  border-radius: 10px;
  box-shadow: 0 0 22px var(--award-glow);
  display: inline-flex;
  gap: 14px;
  max-width: 390px;
  padding: 12px 16px;
}

.passport-award--red {
  --award-color: #f04444;
  --award-glow: rgba(240, 68, 68, 0.18);
}

.passport-award--champion {
  --award-color: #ffd84f;
  --award-glow: rgba(255, 216, 79, 0.24);
  background: linear-gradient(135deg, rgba(255, 216, 79, 0.15), rgba(240, 32, 32, 0.08));
}

.passport-award-artwork {
  align-items: center;
  display: flex;
  flex: 0 0 58px;
  height: 58px;
  justify-content: center;
}

.passport-award-artwork img {
  height: 58px;
  object-fit: contain;
  width: 58px;
}

.passport-award-artwork--light {
  background: #fff;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.32);
  padding: 4px;
}

.passport-award-artwork--light img {
  height: 50px;
  width: 50px;
}

.passport-award-artwork .v-icon {
  color: var(--award-color);
  font-size: 3.4rem;
}

.championship-belt {
  align-items: center;
  background: linear-gradient(180deg, #252525, #050505);
  border: 2px solid #4a4a4a;
  border-radius: 7px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
  display: flex;
  height: 27px;
  justify-content: center;
  position: relative;
  width: 62px;
}

.championship-belt::before,
.championship-belt::after {
  background: #d7a910;
  border: 1px solid #ffe278;
  border-radius: 50%;
  content: "";
  height: 13px;
  position: absolute;
  top: 5px;
  width: 13px;
}

.championship-belt::before {
  left: 4px;
}

.championship-belt::after {
  right: 4px;
}

.championship-belt strong {
  align-items: center;
  background: radial-gradient(circle, #fff09a 0, #d7a910 68%, #8a6500 100%);
  border: 2px solid #ffe278;
  border-radius: 50%;
  color: #d71920;
  display: flex;
  font-family: "Oswald", sans-serif;
  font-size: 1.05rem;
  height: 36px;
  justify-content: center;
  position: relative;
  text-shadow: 0 1px #fff4b8;
  width: 36px;
  z-index: 1;
}

.passport-award-copy {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.passport-award-copy span,
.passport-award-copy small {
  color: #b8b8b8;
  font-size: 0.7rem;
}

.passport-award-copy span {
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.passport-award-copy strong {
  color: var(--award-color);
  font-family: "Oswald", sans-serif;
  font-size: 1.05rem;
  line-height: 1.2;
}

.passport-trophy--gold {
  --trophy-color: #f6c94f;
  --trophy-glow: rgba(246, 201, 79, 0.2);
}

.passport-trophy--silver {
  --trophy-color: #c7d0da;
  --trophy-glow: rgba(199, 208, 218, 0.18);
}

.passport-trophy--bronze {
  --trophy-color: #c88752;
  --trophy-glow: rgba(200, 135, 82, 0.2);
}

.passport-trophy-icon {
  align-items: center;
  color: var(--trophy-color);
  display: flex;
  flex: 0 0 50px;
  height: 50px;
  justify-content: center;
  position: relative;
}

.passport-trophy-icon .v-icon {
  color: var(--trophy-color);
  font-size: 3.2rem;
}

.passport-trophy-icon strong {
  color: #101010;
  font-family: "Oswald", sans-serif;
  font-size: 0.9rem;
  left: 50%;
  position: absolute;
  top: 41%;
  transform: translate(-50%, -50%);
}

.passport-trophy-copy {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.passport-trophy-copy span,
.passport-trophy-copy small {
  color: #b8b8b8;
  font-size: 0.7rem;
}

.passport-trophy-copy span {
  font-weight: 900;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.passport-trophy-copy strong {
  color: var(--trophy-color);
  font-family: "Oswald", sans-serif;
  font-size: 1.05rem;
  line-height: 1.2;
}

.title-line {
  color: #f0f0f0;
  font-size: 1.25rem;
  margin-bottom: 12px;
}

.short-quote {
  color: #d7d7d7;
  font-size: 1.05rem;
  max-width: 680px;
}

.passport-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 18px 0;
}

.passport-action-link {
  align-items: center;
  color: #fff !important;
  display: inline-flex;
  font-weight: 800;
  gap: 6px;
  min-height: 38px;
  text-decoration: none;
}

.passport-action-link {
  background: #0a66c2;
  border-radius: 6px;
  padding: 8px 14px;
}

.passport-action-link:hover {
  background: #084f97;
}

.origin-action-link {
  background: #bcecff;
  color: #07111a !important;
}

.origin-action-link:hover {
  background: #91dcfa;
}

.passport-action-link:focus-visible {
  outline: 3px solid #fff;
  outline-offset: 3px;
}

@keyframes passport-edge-pulse {
  0%,
  100% {
    box-shadow: 0 0 10px rgba(92, 205, 255, 0.18);
    opacity: 0.45;
  }
  50% {
    box-shadow: 0 0 28px rgba(92, 205, 255, 0.62);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .passport-portrait-card {
    transition: none;
  }

  .passport-portrait-card:hover,
  .passport-portrait-card:focus-visible {
    transform: none;
  }

  .passport-portrait-card.is-flipped:hover,
  .passport-portrait-card.is-flipped:focus-visible {
    transform: rotateY(180deg);
  }

  .passport-portrait-card.is-flipped::after {
    animation: none;
    box-shadow: 0 0 14px rgba(92, 205, 255, 0.35);
  }
}

.detail-grid {
  grid-template-columns: repeat(4, minmax(130px, 1fr));
  margin-top: 22px;
}

.detail-item,
.stat-tile,
.stamp-card,
.favorite-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
}

.detail-item strong,
.stat-tile strong,
.favorite-card strong {
  color: #fff;
  display: block;
  font-size: 1.05rem;
  margin-top: 6px;
}

.detail-item small {
  color: #cfcfcf;
  display: block;
  margin-top: 4px;
}

.passport-section {
  margin-top: 36px;
}

.section-heading {
  margin-bottom: 16px;
}

.section-heading h2 {
  color: #fff;
  font-size: 1.8rem;
  letter-spacing: 0;
  margin: 0;
}

.section-kicker {
  color: #e23232;
  font-weight: 900;
  margin: 0 0 4px;
}

.stat-grid {
  grid-template-columns: repeat(4, minmax(150px, 1fr));
}

.stat-tile .v-icon {
  color: #e23232;
}

.stat-link {
  color: inherit;
  position: relative;
  text-decoration: none;
  transition: border-color 160ms ease, transform 160ms ease, background 160ms ease;
}

.stat-link:hover,
.stat-link:focus-visible {
  background: rgba(226, 50, 50, 0.12);
  border-color: rgba(226, 50, 50, 0.65);
  outline: none;
  transform: translateY(-2px);
}

.stat-link .stat-arrow {
  bottom: 14px;
  font-size: 18px;
  position: absolute;
  right: 14px;
}

.stat-tile span {
  color: #cfcfcf;
  display: block;
  font-size: 0.9rem;
  margin-top: 12px;
}

.stamp-grid {
  grid-template-columns: repeat(3, minmax(180px, 1fr));
}

.stamp-card h3 {
  color: #fff;
  font-size: 1.05rem;
  letter-spacing: 0;
  margin: 8px 0;
}

.stamp-card p,
.stamp-card span {
  color: #d4d4d4;
  margin: 0;
}

.stamp-card,
.favorite-card {
  color: inherit;
  position: relative;
  text-decoration: none;
  transition: border-color 160ms ease, transform 160ms ease, background 160ms ease;
}

.stamp-card:hover,
.stamp-card:focus-visible,
.favorite-card:hover,
.favorite-card:focus-visible {
  background: rgba(226, 50, 50, 0.12);
  border-color: rgba(226, 50, 50, 0.65);
  outline: none;
  transform: translateY(-2px);
}

.card-arrow {
  bottom: 14px;
  color: #e23232 !important;
  font-size: 18px;
  position: absolute;
  right: 14px;
}

.two-column-section {
  grid-template-columns: minmax(0, 1fr) minmax(280px, 0.65fr);
}

.badge-list {
  grid-template-columns: repeat(2, minmax(180px, 1fr));
}

.passport-badge-card {
  background: #101010;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  color: #fff !important;
  display: block;
  overflow: hidden;
  text-decoration: none;
  transition: border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;
}

.passport-badge-artwork {
  align-items: center;
  aspect-ratio: 1;
  background: radial-gradient(circle, #242424 0, #080808 72%);
  display: flex;
  justify-content: center;
  overflow: hidden;
  width: 100%;
}

.passport-badge-image {
  clip-path: circle(48.5% at 50% 50%);
  height: 100%;
  transform: scale(1.04);
  width: 100%;
}

.passport-badge-placeholder {
  color: #f0b13c !important;
  font-size: clamp(4.5rem, 10vw, 7rem) !important;
}

.passport-badge-title {
  align-items: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  font-weight: 800;
  gap: 12px;
  justify-content: space-between;
  min-height: 66px;
  padding: 14px 16px;
}

.passport-badge-title .v-icon {
  color: #e23232;
  flex: 0 0 auto;
}

.passport-badge-card:hover,
.passport-badge-card:focus-visible {
  border-color: rgba(226, 50, 50, 0.8);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.36);
  outline: none;
  transform: translateY(-3px);
}

.favorite-grid {
  grid-template-columns: 1fr;
}

.passport-missing {
  background: #101010;
  border-radius: 8px;
  padding: 48px;
  text-align: center;
}

.passport-missing h1 {
  color: #fff;
}

@media (max-width: 960px) {
  .passport-hero-content,
  .two-column-section {
    grid-template-columns: 1fr;
  }

  .detail-grid,
  .stat-grid,
  .stamp-grid,
  .badge-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 600px) {
  .passport-page {
    padding-top: 28px;
  }

  .passport-hero {
    padding: 18px;
  }

  .passport-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .back-link {
    align-self: flex-start;
  }

  .crew-passport-nav {
    width: 100%;
  }

  .passport-nav-link {
    flex: 1 1 0;
    min-width: 0;
  }

  .passport-brand-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .detail-grid,
  .stat-grid,
  .stamp-grid,
  .badge-list {
    grid-template-columns: 1fr;
  }
}
</style>
